import { BackstageOverrides } from '@backstage/core-components';
import { alpha, Theme } from '@mui/material/styles';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../tokens';

export const styledBackstageHeaderTabs = (
  theme: Theme,
): BackstageOverrides['BackstageHeaderTabs'] => {
  const tokens = getThemeTokens(theme);

  return {
    tabsWrapper: {
      alignItems: 'stretch',
      backdropFilter: 'blur(16px)',
      backgroundColor: alpha(getSurfaceColor(theme, 'raised'), 0.9),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: radii.lg,
      boxSizing: 'border-box',
      display: 'flex',
      gridArea: 'pageSubheader',
      marginLeft: theme.spacing(3),
      marginRight: theme.spacing(3),
      marginTop: theme.spacing(2),
      maxWidth: `calc(100% - ${theme.spacing(6)})`,
      minHeight: theme.spacing(6.75),
      overflow: 'hidden',
      padding: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5),
      width: 'fit-content',
      '& [class*="MuiTabs-root"]': {
        minHeight: 44,
        width: 'auto',
      },
      '& [class*="MuiTabs-scroller"]': {
        minHeight: 44,
      },
      '& [class*="MuiTabs-flexContainer"]': {
        alignItems: 'center',
        borderBottom: 0,
        gap: theme.spacing(0.5),
        padding: 0,
      },
      '& [class*="MuiTabs-indicator"]': {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        borderRadius: radii.md,
        marginLeft: theme.spacing(1.5),
        marginRight: theme.spacing(1.5),
        marginTop: theme.spacing(1.5),
        maxWidth: `calc(100% - ${theme.spacing(3)})`,
        minHeight: theme.spacing(6.5),
        overflowX: 'auto',
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
    },
    tabRoot: {
      flex: '0 0 auto',
      color: tokens.textSecondary,
      fontSize: theme.typography.body1.fontSize,
      fontWeight: 700,
      minHeight: 40,
      minWidth: 0,
      padding: theme.spacing(1, 2),
      position: 'relative',
      borderRadius: radii.md,
      textTransform: 'none',
      transition: 'color 150ms ease, background-color 150ms ease',
      '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.14),
        color: tokens.textPrimary,
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: tokens.textPrimary,
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 40,
        padding: theme.spacing(1, 1.5),
      },
    },
    defaultTab: {
      color: tokens.textSecondary,
      fontSize: theme.typography.body1.fontSize,
      fontWeight: 700,
      minHeight: 40,
      padding: theme.spacing(1, 2),
      textTransform: 'none',
    },
    selected: {
      color: tokens.textPrimary,
    },
  };
};
